import { PopperProps } from '@mui/material'
import SingleDropdown, {
  DropdownValue,
} from 'ext-ui/components/Dropdown/SingleDropdown/SingleDropdown'
import TextInput, {
  TextInputState,
  TextInputVariant,
} from 'ext-ui/components/TextInput/TextInput'
import { TooltipIconProps } from 'ext-ui/components/TooltipIcon/TooltipIcon'
import { useAsyncTextInput } from 'ext-ui/hooks/useAsyncTextInput'
import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { ExpenseCategoriesContext } from 'src/components/ExpenseCategoriesInputBox/ExpenseCategoriesInputBox'
import { ExpenseDetailsSourceIcon } from 'src/components/ExpenseDetailsSourceIcon/ExpenseDetailsSourceIcon'
import { useExtendLocation } from 'src/hooks/locationHooks'
import { useTranslate } from 'src/localization/Trns'
import { ExpenseCategoryResponse } from 'src/queries/organizations/expenseCategories.queries'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'

import { useExpenseCategoryDropdownOptions } from './useExpenseCategoryDropdownOptions'

interface ExpenseCategoryDropdownV2Props {
  category: ExpenseCategoryResponse
  context?: ExpenseCategoriesContext
  selectedValue: DropdownValue | undefined
  onSelect: (selectedValue: DropdownValue) => void | Promise<void>
  onSelectSuccess?: (selectedValue: DropdownValue | undefined) => void
  transactionId?: string
  disabled?: boolean
  readOnly?: boolean
  state?: TextInputState
  placeholder?: string
  label?: string
  errorText?: React.ReactNode
  className?: string
  variant?: TextInputVariant
  testId?: string
  /**
   * custom tooltip element to render next to the label
   */
  tooltip?: React.ReactElement
  /**
   * tooltip icon props to render next to the label
   */
  tooltipProps?: TooltipIconProps
  expenseDetail?: {
    ruleId?: string
    generatedByPrediction?: boolean
    errorMessage?: string
  }
  openOnMount?: boolean
  dropdownContainer?: PopperProps['container']
}

const ExpenseCategoryDropdownV2: React.FC<ExpenseCategoryDropdownV2Props> = (
  props,
): React.ReactElement => {
  // FEATURE FLAGS
  const showTransactionFlyoutRevamp = useSelector(
    selectFeatureFlaggerFromState,
  )?.show_transaction_flyout_revamp

  const state =
    props.category.requiredForTransactions && props.selectedValue === undefined
      ? 'error'
      : props.state

  // CUSTOM TOOLS
  const translate = useTranslate()
  const extendLocation = useExtendLocation()

  // STATE
  const { optionGroups, infiniteScrollProps } =
    useExpenseCategoryDropdownOptions({
      expenseCategoryId: props.category.id,
      transactionId: props.transactionId,
      freeTextAllowed: props.category.freeTextAllowed,
      additionalKey: extendLocation.matchedPath,
    })

  // Async text input hook for free text handling
  const asyncTextInputProps = useAsyncTextInput({
    value: props.selectedValue?.value || '',
    onValueChange: (value: string) => props.onSelect({ value }),
    debounceDelay: 500,
  })

  const tooltip = useMemo(() => {
    const appliedByAutomation =
      !!props.expenseDetail?.ruleId ||
      props.expenseDetail?.generatedByPrediction

    if (!appliedByAutomation) return undefined

    return (
      <ExpenseDetailsSourceIcon
        generatedByPrediction={!!props.expenseDetail?.generatedByPrediction}
        errorMessage={props.expenseDetail?.errorMessage}
      />
    )
  }, [props.expenseDetail])

  const isRequired =
    props.context === ExpenseCategoriesContext.VIRTUAL_CARD
      ? props.category.requiredForVirtualCards
      : props.category.requiredForTransactions

  // free text only categories with no labels
  if (props.category.freeTextAllowed && !props.category.hasLabels) {
    return (
      <TextInput
        variant={props.variant || 'medium'}
        state={state}
        label={props.label}
        placeholder={props.placeholder || translate('Type here')}
        disabled={props.disabled}
        readOnly={props.readOnly}
        className={props.className}
        tooltipPosition={'left'}
        tooltip={tooltip}
        tooltipProps={props.tooltipProps}
        requirement={
          showTransactionFlyoutRevamp
            ? isRequired
              ? 'required'
              : undefined
            : !isRequired
              ? 'optional'
              : undefined
        }
        {...asyncTextInputProps}
        errorText={asyncTextInputProps.errorText ?? props.errorText}
      />
    )
  }

  return (
    <SingleDropdown
      {...(props as any)}
      placeholder={props.placeholder || translate('Type or select from list')}
      optionGroups={optionGroups}
      tooltipPosition={'left'}
      tooltip={tooltip}
      infiniteScrollProps={infiniteScrollProps}
      searchable
      freeTextProps={{
        enabled: props.category.freeTextAllowed,
        existingOptionHeader: translate('Existing Labels'),
        freeTextOptionHeader: translate('New Label'),
      }}
      emptyMessage={{
        title: translate('No active labels'),
        subtitle: translate(
          'Looks like there are no active labels to choose from',
        ),
      }}
      requirement={
        showTransactionFlyoutRevamp
          ? isRequired
            ? 'required'
            : undefined
          : !isRequired
            ? 'optional'
            : undefined
      }
      state={state}
    />
  )
}

export default ExpenseCategoryDropdownV2
